import { Message, MessageProps } from "primereact/message";
import { useState } from "react";
import styled from "styled-components";

/** A warning longer than this is collapsed to two lines with a "Show more" toggle. */
export const WARN_MESSAGE_EXPANDABLE_FROM = 140;

/**
 * An orange notice of the benefits panel: a white box with an orange border, orange text.
 * The three-class selector outranks the theme's `.p-inline-message.p-inline-message-warn`.
 */
const StyledWarnMessage = styled(Message)`
  &.p-inline-message.p-inline-message-warn {
    background: var(--brand-white);
    border: 1px solid currentColor;
    border-radius: 1rem;
  }
`;

const StyledClampedText = styled.span<{ $expanded: boolean }>`
  overflow-wrap: anywhere;
  ${({ $expanded }) =>
    $expanded
      ? ""
      : `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

// primeicons sets `line-height: 1` on its icons; inheriting the text's line height puts the icon on
// the first line of a wrapped warning instead of the top edge of the block.
const StyledIcon = styled.i`
  line-height: inherit;
`;

const StyledToggle = styled.button`
  align-self: flex-start;
  padding: 0;
  background: none;
  border: none;
  color: var(--brand-purple-600);
  cursor: pointer;
`;

/**
 * A long warning (e.g. the engine's substitute note) in a narrow field column would otherwise grow
 * into a tall column and push the fields below it down, so it starts collapsed; the full text stays
 * in the DOM either way.
 */
export function WarnMessage(props: MessageProps) {
  const { text } = props;
  const [expanded, setExpanded] = useState(false);

  if (typeof text !== "string" || text.length <= WARN_MESSAGE_EXPANDABLE_FROM) {
    return <StyledWarnMessage {...props} />;
  }

  return (
    <StyledWarnMessage
      {...props}
      content={
        <div className="flex align-items-start">
          <StyledIcon
            className="p-inline-message-icon pi pi-exclamation-triangle"
            aria-hidden="true"
          />
          <div className="flex flex-column gap-1 min-w-0">
            <StyledClampedText
              className="p-inline-message-text"
              data-testid="warnMessage-text"
              $expanded={expanded}
            >
              {text}
            </StyledClampedText>
            <StyledToggle
              type="button"
              className="text-sm"
              aria-expanded={expanded}
              data-testid="warnMessage-toggle"
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? "Show less" : "Show more"}
            </StyledToggle>
          </div>
        </div>
      }
    />
  );
}
